import { ClientDateHandler } from '../ClientDateHandler/ClientDateHandler';

/**
 * `CommonValidation` provides small, reusable validation helpers used across
 * the client.
 *
 * It centralises common checks for:
 * - emptiness and "has value" semantics
 * - numeric / decimal validation and range checks
 * - format validation via regex (alphabetic, alphanumeric, email, phone,
 *   postcode)
 * - date comparisons and SQL-style min/max date validation
 * - JSON date parsing interoperability via `ClientDateHandler`
 *
 * All methods are static and intentionally side-effect free.
 */
export class CommonValidation {
  static isValidValue(value: null | undefined | string) {
    const isValid = !(value === null || value === undefined || value === '');
    return isValid;
  }

  static isNotEmpty(value: null | undefined | string | Date | number) {
    const notEmpty = !CommonValidation.isEmpty(value);
    return notEmpty;
  }

  static isEmpty(value: null | undefined | string | Date | number) {
    const isEmpty = value === null || value === undefined || value === '';
    return isEmpty;
  }

  static isValidDecimal(value: string) {
    const decimalExpression = /^-{0,1}\d*\.{0,1}\d+$/;
    const isValid = decimalExpression.test(value);
    return isValid;
  }

  static isDecimalWithinRange(valueText: number | string) {
    let withinRange = true;
    let value = 0;

    if (typeof valueText === 'string') {
      value = parseFloat(valueText);
    } else {
      value = valueText;
    }

    if (isNaN(value)) {
      return false;
    }

    if (value > 99999999.0 || value < 0) withinRange = false;

    return withinRange;
  }

  static isInteger(value: number | string | null) {
    if (value == null) return false;

    const noDecimals = value.toString().split('.').length == 1;
    const isValid = CommonValidation.isValidNumeric(value) && noDecimals;

    return isValid;
  }

  static isValidNumeric(value: null | undefined | string | number) {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === 'string') {
      value = Number(value);
    }

    const isValid = !isNaN(value);
    return isValid;
  }

  static isValidAlphabetic(value: string) {
    const alphabeticExpression = /^[ a-zA-Z\.]*$/; //eslint-disable-line
    const isValid = alphabeticExpression.test(value);
    return isValid;
  }

  static isValidAlphaNumeric(value: string) {
    const alphaNumericExpression = /^[ a-zA-Z0-9\.]*$/; //eslint-disable-line
    const isValid = alphaNumericExpression.test(value);
    return isValid;
  }

  static isValidModel(value: string) {
    const alphaNumericExpression = /^[ a-zA-Z0-9()\.]*$/; //eslint-disable-line
    const isValid = alphaNumericExpression.test(value);
    return isValid;
  }

  static isValidEmail(value: string) {
    const emailExpression = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,5}$/; //eslint-disable-line

    const isValid = emailExpression.test(value);
    return isValid;
  }

  static isValidPhone(value: string) {
    const phoneExpression =
      /^\+?(\([0-9]{2,5}\)|[0-9]{2,5})[-\s\.]?[0-9]{6,7}$/; //eslint-disable-line
    const isValid = phoneExpression.test(value);
    return isValid;
  }

  static isValidPostcode(value: string) {
    const postcodeExpression = /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/; //eslint-disable-line
    const isValid = postcodeExpression.test(value.toLowerCase());
    return isValid;
  }

  static isStartDatePriorToEndDate(startDate?: Date, endDate?: Date) {
    if (!startDate || !endDate) return false;

    return startDate < endDate;
  }

  static isStartDateEqualOrPriorToEndDate(startDate?: Date, endDate?: Date) {
    if (!startDate || !endDate) return false;

    return startDate <= endDate;
  }

  static isValidLength(value: string, maxLength: number) {
    if (CommonValidation.isEmpty(value)) return true;

    const isValid = value.length <= maxLength;
    return isValid;
  }

  static getJsonDate(jsonDate?: string) {
    if (!jsonDate) return new Date();

    const cdh = new ClientDateHandler();
    cdh.parseJSON(jsonDate);
    const dateValue = cdh.getLocalDate();
    return dateValue;
  }

  static isNullDateJson(jsonDate: string) {
    if (!jsonDate) return true;
    const jsonDateValue = JSON.parse(jsonDate)?.value;
    return (
      jsonDateValue === null ||
      jsonDateValue === 'null' ||
      jsonDate === '0001-01-01T00:00:00'
    );
  }

  static isDateNull(date: string | Date | undefined, isJson: boolean) {
    if (date == undefined) return true;

    if (isJson) {
      return CommonValidation.isNullDateJson(date as string);
    } else {
      return CommonValidation.isEmpty(date);
    }
  }

  static isValidDate = (value: string | Date | undefined, isJson: boolean) => {
    //null dates are valid dates - should use isDateNull in the validation if checking for mandatory dates
    if (CommonValidation.isDateNull(value, isJson)) {
      return true;
    }

    if (value === undefined) return false;

    const sqlMinDate = new Date('1000-01-01');
    const sqlMaxDate = new Date('9999-12-31');

    if (isJson) {
      let isValidDate = false;
      const parsedDate = JSON.parse(value as string);
      const parsedDateValue: string = parsedDate.value;
      isValidDate =
        new Date(parsedDateValue) >= sqlMinDate &&
        new Date(parsedDateValue) <= sqlMaxDate;

      return isValidDate;
    } else {
      if (typeof value === 'string') {
        const dateVal = new Date(value);

        return dateVal >= sqlMinDate && dateVal <= sqlMaxDate;
      } else {
        const dateVal = value as Date;

        return dateVal >= sqlMinDate && dateVal <= sqlMaxDate;
      }
    }
  };
}
